"use client"

import { motion } from "framer-motion"
import {
  LayoutDashboard,
  UserCheck,
  Users,
  Activity,
  TrendingUp,
  Dumbbell,
  HeartPulse,
  Brain,
  ArrowRight,
  Eye,
  Newspaper,
  DollarSign,
} from "lucide-react"
import { AppLink } from "@/components/navigation/app-link"
import { MarketingHashLink } from "@/components/navigation/marketing-hash-link"

const pillars = [
  {
    icon: LayoutDashboard,
    accent: "from-blue-600 to-cyan-500",
    border: "border-blue-500/30",
    glow: "hover:shadow-blue-500/15",
    btnGlow: "hover:shadow-[0_0_24px_6px_rgba(59,130,246,0.55)]",
    btnBorder: "ring-2 ring-blue-400",
    iconBg: "bg-blue-400",
    title: "The platform",
    intro:
      "One app where players and agents see everything: stats, contracts, finances and career plans, all in one place.",
    cta: { label: "See the app", to: "/platform" },
    features: [
      {
        icon: Activity,
        title: "Performance analytics",
        description: "Track how you perform on and off the pitch.",
      },
      {
        icon: Newspaper,
        title: "Personal branding & news",
        description: "Follow your media presence, visibility and reputation.",
      },
      {
        icon: DollarSign,
        title: "Financial overview",
        description: "Keep contracts, bonuses and earnings in one clear view.",
      },
    ],
  },
  {
    icon: UserCheck,
    accent: "from-indigo-500 to-blue-500",
    border: "border-indigo-500/30",
    glow: "hover:shadow-indigo-500/15",
    btnGlow: "hover:shadow-[0_0_24px_6px_rgba(99,102,241,0.55)]",
    btnBorder: "ring-2 ring-indigo-400",
    iconBg: "bg-indigo-400",
    title: "Personal guidance",
    intro:
      "You get your own agent who knows your situation and is there for every decision, big or small.",
    cta: { label: "Meet our agents", to: "/team#agents" },
    features: [
      {
        icon: UserCheck,
        title: "One-on-one support",
        description: "Always one person you can call, who knows your full story.",
      },
      {
        icon: TrendingUp,
        title: "Long-term planning",
        description: "We think in years, not just transfer windows.",
      },
      {
        icon: Eye,
        title: "Eyes on the pitch",
        description: "Our experts and former pros come to watch you play in person.",
      },
    ],
  },
  {
    icon: Users,
    accent: "from-cyan-500 to-teal-500",
    border: "border-cyan-500/30",
    glow: "hover:shadow-cyan-500/15",
    btnGlow: "hover:shadow-[0_0_24px_6px_rgba(6,182,212,0.55)]",
    btnBorder: "ring-2 ring-cyan-400",
    iconBg: "bg-cyan-400",
    title: "Personal coaching",
    intro:
      "A team of coaches who look after your body and mind, so you stay sharp all season.",
    cta: { label: "Meet our coaches", to: "/team#coaches" },
    features: [
      {
        icon: Dumbbell,
        title: "Fitness coaching",
        description: "Training plans built around your own data.",
      },
      {
        icon: HeartPulse,
        title: "Physical support",
        description: "Stay fit, recover faster, avoid injury.",
      },
      {
        icon: Brain,
        title: "Motivation",
        description: "Drive and focus are part of your performance too.",
      },
    ],
  },
]

export function WhatWeDo() {
  return (
    <section id="offer" className="scroll-mt-24 bg-slate-900 py-20">
      <div className="container mx-auto max-w-6xl px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mx-auto max-w-3xl text-center"
        >
          <span className="text-sm font-semibold uppercase tracking-wider text-blue-400">
            Our services
          </span>
          <h2 className="mt-3 text-3xl font-bold leading-tight tracking-tight text-white md:text-4xl">
            What we offer
          </h2>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
          {pillars.map((pillar, pi) => (
            <motion.div
              key={pillar.title}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: pi * 0.1 }}
              className={[
                "group flex flex-col rounded-2xl border bg-white/5 p-7 transition-all duration-300",
                "hover:bg-white/[0.08] hover:shadow-xl",
                pillar.border,
                pillar.glow,
              ].join(" ")}
            >
              <div
                className={`inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${pillar.iconBg} text-white shadow-lg`}
              >
                <pillar.icon className="h-6 w-6" strokeWidth={1.75} />
              </div>

              <h3 className="mt-5 text-xl font-bold text-white">{pillar.title}</h3>
              <p className="mt-2 min-h-[72px] text-sm leading-relaxed text-slate-300">
                {pillar.intro}
              </p>

              <div className="my-6 h-px w-full bg-white/10" />

              <ul className="flex flex-col gap-4">
                {pillar.features.map((feat) => (
                  <li key={feat.title} className="flex min-h-[60px] items-start gap-3">
                    <div
                      className={`mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${pillar.iconBg} text-white shadow-md`}
                    >
                      <feat.icon className="h-4 w-4" strokeWidth={2} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-100">{feat.title}</p>
                      <p className="mt-0.5 text-xs leading-relaxed text-slate-400">
                        {feat.description}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>

              {pillar.cta &&
                (() => {
                  const className = `mt-10 inline-flex items-center gap-2 self-start rounded-xl ${pillar.iconBg} px-5 py-2.5 text-sm font-semibold text-white shadow-lg transition-all duration-300 hover:scale-[1.03] ${pillar.btnBorder} ${pillar.btnGlow}`
                  const content = (
                    <>
                      {pillar.cta.label}
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )
                  return pillar.cta.to.includes("#") ? (
                    <MarketingHashLink href={pillar.cta.to} className={className}>
                      {content}
                    </MarketingHashLink>
                  ) : (
                    <AppLink href={pillar.cta.to} className={className}>
                      {content}
                    </AppLink>
                  )
                })()}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}
